import { db } from './firebase-config';
import { doc, setDoc, getDoc, updateDoc, getDocs, collection, 
  increment, addDoc, deleteDoc, deleteField, arrayUnion, serverTimestamp, query, orderBy, limit, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { deriveSessionTitle } from 'src/utils/sessionTitle';

// User Sessions
const LAST_LOGGED_IN_UPDATED_KEY = 'lastLoggedInUpdated';

const hasUpdatedLastLoggedIn = () => {
  try {
    return sessionStorage.getItem(LAST_LOGGED_IN_UPDATED_KEY) === 'true';
  } catch (e) {
    // sessionStorage might not be available (e.g., SSR, some private Browse modes)
    return false;
  }
};

const setUpdatedLastLoggedIn = () => {
  try {
    sessionStorage.setItem(LAST_LOGGED_IN_UPDATED_KEY, 'true');
  } catch (e) {
    // Silently ignore if sessionStorage is unavailable
  }
};


export const isUserLoggedIn = async () => {
  const auth = getAuth();
  return new Promise((resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // If user is anonymous, treat as "not logged in" for UI menu purposes.
        // This allows backend sessions for anonymous users but prompts UI for Login/Register.
        if (user.isAnonymous) {
          resolve({
            user,
            isAnonymous: true
          });
        }

        // Proceed only for authenticated (non-anonymous) users
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Update last_logged_in once per browser session if older than 24 hours
            if (!hasUpdatedLastLoggedIn()) {
              const currentDate = new Date();
              let lastLoggedInDate = null;
              if (userData.last_logged_in) {
                // Handle Firestore Timestamp or ISO string for last_logged_in
                if (typeof userData.last_logged_in === 'string') {
                  lastLoggedInDate = new Date(userData.last_logged_in);
                } else if (userData.last_logged_in.toDate) { // Firestore Timestamp
                  lastLoggedInDate = userData.last_logged_in.toDate();
                }
              }

              if (!lastLoggedInDate || (currentDate.getTime() - lastLoggedInDate.getTime()) >= 12 * 60 * 60 * 1000) {
                await updateDoc(userRef, { last_logged_in: currentDate.toISOString() });
              }
              setUpdatedLastLoggedIn();
            }

            const isPaid = userData.is_paid || "N";
            const isPro = userData.is_pro || "N";

            // Voice credit logic for paid users
            if (isPaid === "Y") {
              let voiceCredits = userData.voice_credits;
              let lastCreditResetStr = userData.last_credit_reset;
              let lastCreditResetDate = lastCreditResetStr ? new Date(lastCreditResetStr) : null;

              let defaultCredits = (isPro === "Y") ? 999999 : 300;
              const currentDateISO = new Date().toISOString();
              let updatesForPaidUser = {};
              let needsUpdate = false;

              if (voiceCredits === undefined || !lastCreditResetDate) {
                updatesForPaidUser.voice_credits = defaultCredits;
                updatesForPaidUser.last_credit_reset = currentDateISO;
                needsUpdate = true;
              } else {
                const hoursSinceLastReset = (new Date().getTime() - lastCreditResetDate.getTime()) / (1000 * 60 * 60);
                if (hoursSinceLastReset >= 24) {
                  updatesForPaidUser.voice_credits = defaultCredits;
                  updatesForPaidUser.last_credit_reset = currentDateISO;
                  needsUpdate = true;
                }
              }

              if (needsUpdate) {
                await updateDoc(userRef, updatesForPaidUser);
                // To reflect these updates immediately in the resolved object,
                // you could re-fetch userData here:
                // userData = (await getDoc(userRef)).data() || userData;
              }
            }

            const hasSeedPhrase = !!userData.encryptedSeedPhrase;
            const onboarding_completed = userData.onboarding_completed || false;

            resolve({
              user, // Firebase auth user object
              uid: user.uid,
              isAnonymous: false, // Explicitly false for this path
              hasSeedPhrase,
              onboarding_completed,
              emailVerified: user.emailVerified,
              email: user.email,
              is_paid: userData.is_paid || "N", // Default to "N" if undefined
              is_pro: userData.is_pro || "N",   // Default to "N" if undefined
              voice_credits: userData.voice_credits, // Reflects current DB state (or undefined if not paid)
              last_credit_reset: userData.last_credit_reset, // Reflects current DB state
              encryptedSeedPhrase: userData.encryptedSeedPhrase || null,
              salt: userData.salt || null,
              iv: userData.iv || null,
              totalSavedSession: userData.totalSavedSession || 0,
              totalSessions: userData.totalSessions || 0,
              encryptedConversationCount: userData.encryptedConversationCount || 0,
              username: userData.username || null,
              memory_enabled: userData.memory_enabled || false,
              mindgraph_enabled: userData.mindgraph_enabled !== false, // backend treats missing as enabled
              displayName: userData.displayName || null,
              zosa_admin: userData.zosa_admin || "N",
            });

          } else {
            // Document doesn't exist: create for new non-anonymous authenticated user
            const currentDateISO = new Date().toISOString();
            const newUserDocData = {
              email: user.email,
              emailVerified: user.emailVerified,
              createdAt: currentDateISO,
              last_logged_in: currentDateISO,
              is_paid: "N", // Default for new users
              is_pro: "N",  // Default for new users
              memory_enabled: false,
            };
            await setDoc(userRef, newUserDocData);
            setUpdatedLastLoggedIn(); // Mark as updated for this new user's session

            resolve({
              user,
              uid: user.uid,
              isAnonymous: false,
              hasSeedPhrase: false,
              onboarding_completed: false,
              emailVerified: user.emailVerified,
              email: user.email,
              is_paid: newUserDocData.is_paid,
              is_pro: newUserDocData.is_pro,
              voice_credits: undefined, // New users start with no voice credits defined
              last_credit_reset: null,    // until they become paid.
              encryptedConversationCount: [],
              encryptedSeedPhrase: null,
              salt: null,
              iv: null,
              totalSavedSession: [],
              totalSessions: [],
              blockNumbers: [], 
              username: user.username || null,
              memory_enabled: false,
              displayName: 'Anonymous',
            });
          }
        } catch (error) {
          //console.error("Error processing authenticated user state:", error);
          resolve(null); // Error state
        }
      } else {
        // No Firebase user (neither anonymous nor authenticated)
        resolve(null);
      }
    });
  });
};



export const ensureAuthenticated = async () => {
  const auth = getAuth();
  
  // If a user (real or anonymous) already exists, return them
  if (auth.currentUser) {
    return auth.currentUser;
  }
  
  // Otherwise, sign in anonymously
  try {
    const userCredential = await signInAnonymously(auth);
    return userCredential.user;
  } catch (error) {
    // console.error("Anonymous sign-in failed:", error);
    return null;
  }
};

export const userMemberStatus = async (userId) => {
  if (!userId) {
    // console.warn("userMemberStatus: userId not provided.");
    // Return a default status or null, depending on how you want to handle this
    return { is_pro: "N", is_paid: "N", username: "Anonymous", totalSessions: 0 };
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        is_pro: userData.is_pro || "N", // Default to "N" if undefined
        is_paid: userData.is_paid || "N", // Default to "N" if undefined
        username: userData.displayName || userData.username || "Anonymous",
        totalSessions: userData.totalSessions || 0
      };
    } else {
      // This case means the user document doesn't exist.
      return { is_pro: "N", is_paid: "N", username: "Anonymous", totalSessions: 0 };
    }
  } catch (error) {
    return { is_pro: "N", is_paid: "N", username: "Anonymous", totalSessions: 0 }; 
  }
};

export const saveDisplayNameToFirestore = async (userId, displayName) => {
  if (!userId || !displayName) {
    // console.error("User ID and display name are required to save.");
    return;
  }

  try {
    const userRef = doc(db, 'users', userId);
    // Use setDoc with merge:true to create/update the field without overwriting the document
    await setDoc(userRef, { displayName: displayName }, { merge: true });
  } catch (error) {
    // console.error("Error saving display name to Firestore:", error);
    // Re-throw the error so the calling function can handle it
    throw error;
  }
};

export const updateTotalSessionsInFirestore = async (userEmail, totalSessions) => {
  if (!userEmail) return;

  const userDocRef = doc(db, 'userSessions', userEmail);

  try {
    await setDoc(userDocRef, { totalSessions }, { merge: true });
    updateGlobalTotalSessions(totalSessions);
  } catch (error) {
    //console.error("Error updating total sessions:", error);
  }
};

export const fetchUserSessionsFromFirestore = async (userId) => {
  if (!userId) return null;

  const userSessionRef = doc(db, 'userSessions', userId);
  try {
    const docSnap = await getDoc(userSessionRef);
    return docSnap.exists() ? docSnap.data().sessions : null;
  } catch (error) {
    //console.errorror("Error fetching user sessions:", error);
    return null;
  }
};


export const fetchContinuableSessions = async (userId, sessionType = 'text') => {
  if (!userId) return [];

  // Logic to fetch VOICE sessions
  if (sessionType === 'voice') {
    try {
      const sessionsCollectionRef = collection(db, 'user_sessions', userId, 'sessions');
      const q = query(
        sessionsCollectionRef,
        where('session_type', '==', 'voice'),
        where('saved_summary', '==', true),
        orderBy('last_updated', 'desc'),
        limit(10)
      );

      const querySnapshot = await getDocs(q);
      const sessions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.last_updated?.toDate();
        if (timestamp) {
          sessions.push({
            sessionId: doc.id,
            threadId: doc.id, // for consistency
            timestamp: timestamp.toISOString(),
            title: data.summary_title || `Voice Session - ${timestamp.toLocaleDateString()}`,
            summary: '', // for consistency
          });
        }
      });
      return sessions;
    } catch (error) {
      //console.error('Failed to load voice sessions to continue:', error);
      return [];
    }
  }

  // Default logic to fetch TEXT sessions.
  //
  // Mirrors the mobile app's pre-session picker (`loadPreviousSessions` in
  // zosa-app/app/(app)/chat.tsx): read the most recent session docs by
  // `created_at`, keep the ones that actually hold a resumable conversation,
  // and title them with the shared `deriveSessionTitle`. Keep the two in sync.
  //
  // This replaced a split implementation that merged the legacy
  // `userSessions/{uid}` document with a 3-row window over
  // `user_sessions/{uid}/sessions`. Only the web app ever wrote `userSessions`,
  // so sessions saved on mobile never appeared there, and the 3-row cap meant
  // they aged out of the dropdown entirely after a few new sessions.
  try {
    const sessionsCollectionRef = collection(db, 'user_sessions', userId, 'sessions');
    // Scan well past the 15 rows we keep. Most docs here don't qualify:
    // create_session writes `encrypted_conversation: ""`, so every abandoned
    // session — plus the checkin/scheduled/playbook docs the crons create — is
    // filtered out below and would otherwise starve the window.
    const q = query(sessionsCollectionRef, orderBy('created_at', 'desc'), limit(80));
    const querySnapshot = await getDocs(q);

    const sessions = [];

    querySnapshot.forEach((docSnap) => {
      const s = docSnap.data();

      // A long session has its transcript moved into chunk docs: the backend
      // deletes `encrypted_conversation` from the parent and sets `is_chunked`
      // (main.py `write_encrypted_data_chunked`). Testing the blob alone hides
      // every chunked session — mobile has this same gap and should be fixed too.
      const hasTranscript = !!(s.encrypted_conversation || s.is_chunked);
      const hasContent = (hasTranscript && (s.visible_user_turns ?? 0) >= 1) || s.manually_closed;

      // `checkin` sessions are a separate daily flow, not something to resume.
      if (!hasContent || s.session_type === 'voice' || s.session_type === 'checkin') {
        return;
      }

      // Show WHEN THE SESSION WAS LAST ACTIVE, not when it was created. A
      // conversation resumed or re-encrypted weeks later was reading as its
      // original date here, which made this list disagree with ZoSecure
      // Conversations (which dates by encryption) and looked like sessions
      // were missing. Falls back to created_at for docs with no last_updated.
      const lastUpdated = s.last_updated?.toDate ? s.last_updated.toDate() : null;
      const createdAt = s.created_at?.toDate ? s.created_at.toDate() : null;
      const timestamp = lastUpdated || createdAt;
      if (!timestamp) return;

      sessions.push({
        sessionId: docSnap.id,
        threadId: docSnap.id,
        timestamp: timestamp.toISOString(),
        title: deriveSessionTitle(s),
        summary: s.initial_summary || '',
      });
    });

    return sessions
      // Legacy Assistants-API threads can't be resumed by the current backend.
      .filter(s => s.threadId && !s.threadId.startsWith('thread_'))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      // 50, not 15. The old cap silently hid resumable sessions from anyone with
      // a long history — a user with 19 lost her four oldest. The query above
      // already fetches 80 rows, so this costs nothing extra.
      .slice(0, 50);
  } catch (error) {
    // console.error('Failed to load sessions to continue:', error);
    return [];
  }
};

export const updateUserTotalSavedSession = async (userId) => {
  if (!userId) return;
  try {
    // Get the user sessions document from the "userSessions" collection
    const userSessionsRef = doc(db, 'userSessions', userId);
    const docSnap = await getDoc(userSessionsRef);

    let sessionCount = 0;
    if (docSnap.exists()) {

      const sessionsData = docSnap.data().sessions || {};
      sessionCount = Object.keys(sessionsData).length;
    }

    // Update the user's document in the "users" collection with the totalSavedSession field
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      totalSavedSession: sessionCount,
    });
  } catch (error) {
    //console.error('Error updating totalSavedSession:', error);
  }
};


export const saveSessionToFirestore = async (userId, sessionName, sessionData) => {
  if (!userId || !sessionName || !sessionData) return;
  
  const userSessionRef = doc(db, 'userSessions', userId);

  try {
    const docSnap = await getDoc(userSessionRef);
    let existingSessions = docSnap.exists() ? docSnap.data().sessions || {} : {};

    // Append ISO timestamp to ensure uniqueness
    const shortId = uuidv4().split('-')[0]; // e.g., "f47ac10b"
    const uniqueSessionName = `${sessionName}-${shortId}`;

    existingSessions[uniqueSessionName] = {
      summaries: [sessionData]
    };

    await setDoc(userSessionRef, { userId, sessions: existingSessions }, { merge: true });

    await updateUserTotalSavedSession(userId);

   const { threadId } = sessionData;
   if (threadId) {
    const sessionDocRef = doc(db, 'user_sessions', userId, 'sessions', threadId);
    await updateDoc(sessionDocRef, {
      session_summary_saved: true,
      saved_summary: true
    });
  }

    return uniqueSessionName;
  } catch (error) {
    // console.error("Error saving session to Firestore:", error);
  }
};


export const markSessionEncrypted = async (userId, sessionId) => {
  if (!userId || !sessionId) return;
  const ref = doc(db, 'user_sessions', userId, 'sessions', sessionId);
  await updateDoc(ref, { conversation_encrypted: 'Y' });   // flag only
};


export const deleteUserSessionFromFirestore = async (userId, sessionKey, indexToDelete) => {
  if (!userId || !sessionKey || indexToDelete === undefined) return;

  const userSessionRef = doc(db, 'userSessions', userId);

  try {
    const docSnap = await getDoc(userSessionRef);
    if (docSnap.exists()) {
      const sessions = docSnap.data().sessions || {};
      const summaries = sessions[sessionKey]?.summaries;

      if (summaries && Array.isArray(summaries)) {
        summaries.splice(indexToDelete, 1); // Remove the specific summary

        if (summaries.length === 0) {
          delete sessions[sessionKey]; // Delete the entire sessionKey if no summaries left
        } else {
          sessions[sessionKey].summaries = summaries;
        }

        // Update Firestore
        await setDoc(userSessionRef, { sessions }, { merge: false });
        await updateUserTotalSavedSession(userId);
      }
    }
  } catch (error) {
    //console.error("Error deleting session summary from Firestore:", error);
  }
};




export const updateUserActionCntInFirestore = async (userId, newTasks, sessionThread) => {
  if (!userId) return;

  const userDocRef = doc(db, 'usersActionCnt', userId); // Use userId instead of userEmail

  try {
      const docSnap = await getDoc(userDocRef);
      let existingTasks = docSnap.exists() && docSnap.data().totalActions ? docSnap.data().totalActions : [];

      const tasksMap = new Map(existingTasks.map(task => [task.id, task]));

      newTasks.forEach(task => {
          tasksMap.set(task.id, task);
      });

      const updatedTasks = Array.from(tasksMap.values());

      await setDoc(userDocRef, { totalActions: updatedTasks }, { merge: true });
      const sessionDocRef = doc(db, 'user_sessions', userId, 'sessions', sessionThread);
      await updateDoc(sessionDocRef, { 
        session_tasks_saved: true,
        post_session_actions: "Y" 
      });
      // console.log("Tasks updated successfully");
  } catch (error) {
      //console.errorror("Error updating tasks:", error);
  }
};

export const deleteUserActionCntInFirestore = async (userId, taskIdToDelete) => {
  if (!userId) {
    return;
  }
  
  const userDocRef = doc(db, 'usersActionCnt', userId);

  try {
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      const existingTasks = docSnap.data().totalActions || [];
      const updatedTasks = existingTasks.filter(task => task.id !== taskIdToDelete);

      // Overwrite the document with the updated array of tasks
      await setDoc(userDocRef, { totalActions: updatedTasks }, { merge: false });
    }
  } catch (error) {
    //console.error("Error deleting task:", error);
  }
};


export const fetchUserActionCntFromFirestore = async (userId) => {
  const userDocRef = doc(db, 'usersActionCnt', userId); // Use userId instead of userEmail
  try {
      const docSnap = await getDoc(userDocRef);
      return docSnap.exists() ? docSnap.data().totalActions : null;
  } catch (error) {
      //console.error("Error fetching user sessions:", error);
      return null;
  }
};


// Total Sessions
export const updateGlobalTotalSessions = async (newSessions) => {
  const totalSessionsRef = doc(db, 'totalUserSessions', 'globalCount');

  try {
    const snap = await getDoc(totalSessionsRef);
    const data = snap.exists() ? snap.data() : {};
    const currentTotal = Number(data.totalSessions) || 0;
    const additionalSessions = Number(newSessions);
    await updateDoc(totalSessionsRef, { totalSessions: currentTotal + additionalSessions });
  } catch (error) {
    //console.error("Error updating global total sessions:", error);
  }
};

export const updateGlobalTotalVoiceSessions = async (newSessions) => {
  const totalSessionsRef = doc(db, 'totalUserSessions', 'globalVoiceCount');
  try {
    const snap = await getDoc(totalSessionsRef);
    const data = snap.exists() ? snap.data() : {};
    const currentTotal = Number(data.totalSessions) || 0;
    const additionalSessions = Number(newSessions);
    await updateDoc(totalSessionsRef, { totalSessions: currentTotal + additionalSessions });
  } catch (error) {
    //console.error("Error updating global total sessions:", error);
  }
};

export const fetchUsersBlockNumbers = async (userId) => {
  const userDocRef = doc(db, 'users', userId);
  try {
    const docSnap = await getDoc(userDocRef);
    return docSnap.exists() ? docSnap.data().blockNumbers : null;
  } catch (error) {
    //console.error("Error fetching user block numbers:", error);
    return null;
  }
};

export const fetchBlockTimestamps = async (blockIndexes) => {
  try {
    const timestamps = await Promise.all(
      blockIndexes.map(async (index) => {
        const blockDocRef = doc(db, 'blockchain', String(index)); // Get the document by index
        const docSnap = await getDoc(blockDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          return { index, timestamp: data.timestamp }; // Ensure the field matches the Firestore structure
        }

        return { index, timestamp: null }; // Return null if not found
      })
    );

    return timestamps;
  } catch (error) {
    //console.error('Error fetching block timestamps:', error);
    return [];
  }
};

export const saveDetailedFeedback = async (feedbackData) => {
  if (!feedbackData || !feedbackData.feedbackText) {
    throw new Error("Feedback data is invalid.");
  }

  try {
    // This creates a reference to the 'feedback_submissions' subcollection
    // which lives under the 'totalFeedback' document.
    const feedbackSubmissionsRef = collection(db, 'sessionFeedback', 'totalFeedback', 'feedback_submissions');
    
    // Add a new document. Firestore automatically generates a unique ID.
    await addDoc(feedbackSubmissionsRef, {
      ...feedbackData,
      submittedAt: serverTimestamp(), // Use Firestore's server timestamp for accuracy and ordering
    });

  } catch (error) {
    // console.error("Error saving detailed feedback to Firestore:", error);
    // Re-throw the error to be handled by the calling function
    throw error;
  }
};

// --- Case Study Feedback ---
// Prompts long-term, active users to share a testimonial we can use as a case study.
// Only runs while CASE_STUDY_CUTOFF hasn't passed; re-prompts every CASE_STUDY_REPROMPT_DAYS
// until the user answers. Mirrors the mobile app's firebaseService.ts implementation
// (see CaseStudyFeedbackDialog for the TEST_MODE bypass).

const CASE_STUDY_MIN_ACCOUNT_AGE_DAYS = 30; // "with ZOSA a month or two"
const CASE_STUDY_MIN_SESSIONS = 5; // "over 5 total sessions"
const CASE_STUDY_REPROMPT_DAYS = 25;
const CASE_STUDY_CUTOFF = new Date('2027-02-28T23:59:59Z');

/**
 * Whether the case-study feedback prompt should be shown to this user right now.
 * @param {string} uid
 * @param {string|Date|null} accountCreatedAt - e.g. currentUser.metadata.creationTime
 * @param {number} totalSessions
 * @returns {Promise<boolean>}
 */
export const shouldShowCaseStudyPrompt = async (uid, accountCreatedAt, totalSessions) => {
  if (!uid) return false;
  try {
    if (Date.now() > CASE_STUDY_CUTOFF.getTime()) return false;
    if (!totalSessions || totalSessions <= CASE_STUDY_MIN_SESSIONS) return false;

    const createdMs = accountCreatedAt ? new Date(accountCreatedAt).getTime() : NaN;
    if (!createdMs || Date.now() - createdMs < CASE_STUDY_MIN_ACCOUNT_AGE_DAYS * 24 * 60 * 60 * 1000) {
      return false;
    }

    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    const data = userDoc.exists() ? userDoc.data() : {};
    if (data.case_study_prompt_answered === true) return false;

    const lastShown = data.case_study_prompt_last_shown;
    if (lastShown?.toDate && Date.now() - lastShown.toDate().getTime() < CASE_STUDY_REPROMPT_DAYS * 24 * 60 * 60 * 1000) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const markCaseStudyPromptShown = async (uid) => {
  if (!uid) return;
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { case_study_prompt_last_shown: serverTimestamp() }, { merge: true });
  } catch (error) {
    // console.error("Error setting case_study_prompt_last_shown:", error);
  }
};

export const markCaseStudyPromptAnswered = async (uid) => {
  if (!uid) return;
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { case_study_prompt_answered: true }, { merge: true });
  } catch (error) {
    // console.error("Error setting case_study_prompt_answered:", error);
  }
};

export const saveCaseStudyFeedback = async (feedbackData) => {
  if (!feedbackData || !feedbackData.feedbackText) {
    throw new Error("Case study feedback data is invalid.");
  }
  try {
    const ref = collection(db, 'sessionFeedback', 'caseStudyFeedback', 'submissions');
    await addDoc(ref, {
      ...feedbackData,
      submittedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

// --- Public Reviews (/reviews page) ---
// Curated, admin-written testimonials only (see scripts/seed_public_reviews.py) —
// never populated directly from raw feedback collections, so adding a new one
// is a Firestore write, not a code change or redeploy.
export const getPublicReviews = async () => {
  try {
    // firestore.rules can only allow a `list` request when the query itself
    // constrains on the same field the rule checks (active == true) — without
    // this `where`, Firestore can't verify every possible result satisfies the
    // rule and denies the whole query. Sorting is done client-side afterwards
    // so this stays a single-field-equality query and needs no composite index.
    const q = query(collection(db, 'public_reviews'), where('active', '==', true));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      // Enforced floor, not just curation discipline: a rating below 4 never
      // shows even if a low one is added to Firestore later. Unrated mentions
      // (e.g. the Reddit quote, which has no star rating at all) still pass.
      .filter((r) => typeof r.rating !== 'number' || r.rating >= 4)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  } catch (error) {
    console.error('Failed to load public reviews:', error);
    return [];
  }
};


//Session Feedback
export const updateSessionFeedback = async (feedbackType) => {
  const totalFeedbackRef = doc(db, 'sessionFeedback', 'totalFeedback');

  try {
    const docSnap = await getDoc(totalFeedbackRef);
    let positive = 0;
    let negative = 0;

    if (docSnap.exists()) {
      const currentData = docSnap.data();
      positive = currentData.positive || 0;
      negative = currentData.negative || 0;
    }

    const updates = {};
    if (feedbackType === 'positive') {
      updates.positive = positive + 1;
    } else if (feedbackType === 'negative') {
      updates.negative = negative + 1;
    }

    // Use setDoc with merge:true to create the document if it doesn't exist, or update it if it does.
    await setDoc(totalFeedbackRef, updates, { merge: true });

  } catch (error) {
    // console.error('Error updating aggregate feedback count: ', error);
  }
};

// Function to save a block to Firestore
export const saveBlockToFirestore = async (block) => {
  const blockRef = doc(db, 'blockchain', String(block.index));
  await setDoc(blockRef, block, { merge: true });
};

// Function to fetch the entire blockchain from Firestore
export const fetchBlockchainFromFirestore = async () => {
  const querySnapshot = await getDocs(collection(db, 'blockchain'));
  const blockchain = [];
  querySnapshot.forEach((doc) => {
    blockchain.push(doc.data());
  });
  return blockchain;
};

export const saveSeedPhraseToFirestore = async (userId, encryptedSeedPhrase, passphraseHint, salt, iv) => {
  if (!userId || !encryptedSeedPhrase) return;

  const userDocRef = doc(db, 'users', userId);

  try {
    await setDoc(userDocRef, { encryptedSeedPhrase, passphraseHint, salt, iv }, { merge: true });
    // console.log("Encrypted seed phrase saved successfully");
  } catch (error) {
    //console.error("Error saving encrypted seed phrase:", error);
  }
};

export const getEncryptedSeedPhraseFromFirestore = async (userId) => {
  if (!userId) return null;

  const userDocRef = doc(db, 'users', userId);

  try {
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      const { encryptedSeedPhrase, salt, iv, passphraseHint } = docSnap.data();
      return { encryptedSeedPhrase, salt, iv, passphraseHint };
    } else {
      return null;
    }
  } catch (error) {
    //console.error("Error fetching encrypted seed phrase:", error);
    return null;
  }
};

// Save encryption key to Firestore
export const saveEncryptionKeyToFirestore = async (userId, encryptionKey) => {
  const userDocRef = doc(db, 'users', userId);
  await setDoc(userDocRef, { encryptionKey }, { merge: true });
};

// Save user settings to Firestore
export const saveUserSettingsToFirestore = async (userId, settings) => {
  if (!userId || !settings) return;

  const settingsDocRef = doc(db, 'userSettings', userId);
  const userDocRef = doc(db, 'users', userId);

  try {
    await setDoc(settingsDocRef, settings, { merge: true });
    const userSyncData = { memory_enabled: settings.long_term_memory === 'Y' };
    if (settings.mindgraph_enabled !== undefined) {
      userSyncData.mindgraph_enabled = settings.mindgraph_enabled;
    }
    if (settings.nd_identity !== undefined) {
      userSyncData.nd_identity = settings.nd_identity;
    }
    if (settings.special_interests !== undefined) {
      userSyncData.special_interests = settings.special_interests;
    }
    await setDoc(userDocRef, userSyncData, { merge: true });
    // console.log("User settings saved successfully");
  } catch (error) {
    //console.errorror("Error saving user settings:", error);
  }
};


export const updateSessionToolPreference = async (userId, sessionId, toolSuggestionValue) => {
  if (!userId || !sessionId) return;
  
  try {
    const sessionRef = doc(db, 'user_sessions', userId, 'sessions', sessionId);
    await updateDoc(sessionRef, {
      tool_suggestions: toolSuggestionValue,
      last_updated: serverTimestamp() 
    });
    // console.log(`Updated session ${sessionId} tool_suggestions to ${toolSuggestionValue}`);
  } catch (error) {
    // console.error("Error updating session tool preference:", error);
  }
};

// Fetch user settings from Firestore
export const fetchUserSettingsFromFirestore = async (userId) => {
  if (!userId) return null;

  const settingsDocRef = doc(db, 'userSettings', userId);

  try {
    const docSnap = await getDoc(settingsDocRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    //console.error("Error fetching user settings:", error);
    return null;
  }
};

// App release promo cooldown.
// Stored on the user's settings doc rather than only in localStorage: a browser
// storage clear (or simply signing in on another browser/device) used to reset
// the 7 day gap and show the promo again straight away.
export const fetchAppPromoLastShown = async (userId) => {
  if (!userId) return null;

  try {
    const docSnap = await getDoc(doc(db, 'userSettings', userId));
    if (!docSnap.exists()) return null;
    const value = docSnap.data().appPromoLastShownAt;
    return typeof value === 'number' ? value : null;
  } catch (error) {
    // Offline / rules failure — the caller falls back to the local copy.
    return null;
  }
};

export const recordAppPromoShown = async (userId, shownAt) => {
  if (!userId) return;

  try {
    await setDoc(
      doc(db, 'userSettings', userId),
      { appPromoLastShownAt: shownAt },
      { merge: true }
    );
  } catch (error) {
    // Non-fatal: the local copy still holds the cooldown for this browser.
  }
};

export const deleteUserMemories = async (userId) => {
  if (!userId) {
    // console.error("User ID is required to delete memories.");
    return;
  }
  try {
    // Construct a reference to the document using the userId
    const memoryDocRef = doc(db, 'user_memory', userId);
    
    // Delete the document
    await deleteDoc(memoryDocRef);
    
    // console.log(`Memories for user ${userId} have been deleted.`);
  } catch (error) {
    // It's not an error if the document doesn't exist, so we can ignore 'not-found' errors.
    if (error.code !== 'not-found') {
      // console.error("Error deleting user memories:", error);
      throw error; // Re-throw other errors to be handled by the UI
    }
  }
};


export const getAllSymptomGraphSnapshots = async (userId) => {
  if (!userId) return [];
  try {
    const ref = collection(db, 'user_symptom_graph', userId, 'snapshots');
    const q = query(ref, orderBy('created_at', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
};

export const clearMindGraph = async (userId) => {
  if (!userId) return;
  try {
    // Delete all snapshots first
    const snapshotsRef = collection(db, 'user_symptom_graph', userId, 'snapshots');
    const snap = await getDocs(snapshotsRef);
    const deletions = snap.docs.map((d) => deleteDoc(d.ref));
    await Promise.all(deletions);

    // Delete the main graph document
    await deleteDoc(doc(db, 'user_symptom_graph', userId));
  } catch (error) {
    if (error.code !== 'not-found') throw error;
  }
};

export const updateUserSessionInFirestore = async (userId, sessionKey, updatedSummaries) => {
  if (!userId || !sessionKey) return;
  const userSessionRef = doc(db, 'userSessions', userId);
  try {
    await updateDoc(userSessionRef, {
      [`sessions.${sessionKey}.summaries`]: updatedSummaries
    });
  } catch (error) {
    //console.error("Error updating session in Firestore:", error);
  }
};


export const saveRememberedMessageToFirestore = async (userId, message) => {
  if (!userId || !message) return;

  const userDocRef = doc(db, 'users', userId);

  try {
      const docSnap = await getDoc(userDocRef);
      let rememberUserInfo = docSnap.exists() && docSnap.data().rememberUserInfo ? docSnap.data().rememberUserInfo : [];

      const newMessage = {
          id: uuidv4(),  // Generate a unique ID for the message
          message: message
      };

      rememberUserInfo.push(newMessage);

      await setDoc(userDocRef, { rememberUserInfo }, { merge: true });
      // console.log('Remembered message saved successfully');
  } catch (error) {
      //console.error('Error saving remembered message:', error);
  }
};


// Fetch remembered info from Firestore
export const fetchRememberedInfoFromFirestore = async (userId) => {
  if (!userId) return [];

  const userDocRef = doc(db, 'users', userId);

  try {
      const docSnap = await getDoc(userDocRef);
      return docSnap.exists() ? docSnap.data().rememberUserInfo || [] : [];
  } catch (error) {
      //console.error("Error fetching remembered info:", error);
      return [];
  }
};

// Delete a remembered message from Firestore
export const deleteRememberedInfoFromFirestore = async (userId, messageId) => {
  if (!userId || !messageId) {
    return;
  }

  const userDocRef = doc(db, 'users', userId);

  try {
      const docSnap = await getDoc(userDocRef);
      if (!docSnap.exists()) {
          return;
      }

      let rememberUserInfo = docSnap.data().rememberUserInfo || [];

      // Check if the messageId exists in the array
      if (!rememberUserInfo.some(info => info.id === messageId)) {
          return;
      }

      // Filter out the message with the provided messageId
      rememberUserInfo = rememberUserInfo.filter(info => info.id !== messageId);

      // Update the document with the new array
      await setDoc(userDocRef, { rememberUserInfo }, { merge: true });
  } catch (error) {
  }
};

export const updateRememberedInfoInFirestore = async (userId, messageId, updatedMessage) => {
  if (!userId || !messageId) return;

  const userDocRef = doc(db, 'users', userId);

  try {
    const docSnap = await getDoc(userDocRef);
    if (!docSnap.exists()) return;

    const rememberUserInfo = docSnap.data().rememberUserInfo || [];
    if (!rememberUserInfo.some((info) => info.id === messageId)) return;

    const updated = rememberUserInfo.map((info) =>
      info.id === messageId ? { ...info, message: updatedMessage } : info
    );

    await setDoc(userDocRef, { rememberUserInfo: updated }, { merge: true });
  } catch (error) {
    // console.error("Error updating remembered info:", error);
  }
};

export const createUserPetDocument = async (userId) => {
  try {
    const userPetDocRef = doc(db, 'user_pet', userId);

    // Check if the pet document already exists
    const docSnap = await getDoc(userPetDocRef);
    if (!docSnap.exists()) {
      await setDoc(userPetDocRef, {
        userId: userId,
        has_pet: 'Y',
        // Initialize other pet properties if needed
      });
      // console.log('User pet document created successfully');
    } else {
      // console.log('User pet document already exists');
    }
    return true; // Return true to indicate success
  } catch (error) {
    //console.error('Error creating user pet document:', error);
    return false; // Return false to indicate failure
  }
};


export const addPointsToUserPet = async (userId, pointsToAdd = 100) => {
  if (!userId) return;

  const userPetDocRef = doc(db, 'user_pet', userId);

  try {
    const docSnap = await getDoc(userPetDocRef);
    if (docSnap.exists()) {
      const petData = docSnap.data();
      if (petData.has_pet === 'Y') {
        const currentPoints = petData.points || 0;
        const newPoints = currentPoints + pointsToAdd;

        await updateDoc(userPetDocRef, { points: newPoints });
      }
    }
  } catch (error) {
    //console.error('Error updating user pet points:', error);
  }
};


export const updateUserSeedPhrase = async (userId, { encryptedSeedPhrase, salt, iv, passphraseHint }) => {
  try {
    const userRef = doc(db, 'users', userId); // Reference to the user's document
    await updateDoc(userRef, {
      encryptedSeedPhrase,
      salt,
      iv,
      passphraseHint,
      emailVerified: true,
    });
    // console.log('Seed phrase updated successfully.');
  } catch (error) {
    //console.error('Error updating user seed phrase:', error);
    throw error;
  }
};


// ── Encrypted conversations ──────────────────────────────────────────────────
// Each conversation is its own document at user_conversations/{uid}/items/{threadId}.
// They used to all live in one `conversations` array on user_conversations/{uid},
// which hit Firestore's 1 MiB per-document limit once a user had ~13 of them.
// Reads still merge in that legacy array so nothing is lost before migration.
const conversationItemsRef = (userId) =>
  collection(db, 'user_conversations', userId, 'items');

const readLegacyConversations = async (userId) => {
  const snap = await getDoc(doc(db, 'user_conversations', userId));
  if (!snap.exists()) return [];
  const arr = snap.data()?.conversations;
  return Array.isArray(arr) ? arr : [];
};

const toMillis = (v) => {
  if (!v) return 0;
  if (v.toDate) return v.toDate().getTime();
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * When a conversation was most recently encrypted.
 * Re-encrypting appends to `encryptedDates`, so the newest entry there is the
 * truth. Falls back to createdAt for records written before that field existed.
 */
export const lastEncryptedAtMs = (convo) => {
  const dates = Array.isArray(convo?.encryptedDates) ? convo.encryptedDates : [];
  const newest = dates.reduce((max, d) => Math.max(max, toMillis(d)), 0);
  return newest || toMillis(convo?.createdAt);
};

/**
 * Every encrypted conversation for a user, most recently encrypted first.
 * Merges items/ with the legacy array; items/ wins on a duplicate threadId.
 */
export const getEncryptedConversationsList = async (userId) => {
  if (!userId) return [];
  const byId = new Map();

  for (const entry of await readLegacyConversations(userId)) {
    const tid = entry.threadId || entry.id;
    if (tid) byId.set(tid, { ...entry, threadId: tid, id: tid, _source: 'legacy' });
  }
  const itemsSnap = await getDocs(conversationItemsRef(userId));
  itemsSnap.forEach((d) => {
    const data = d.data();
    byId.set(d.id, { ...data, threadId: d.id, id: d.id, _source: 'items' });
  });

  return [...byId.values()]
    .map((c) => ({ ...c, lastEncryptedAtMs: lastEncryptedAtMs(c) }))
    .sort((a, b) => b.lastEncryptedAtMs - a.lastEncryptedAtMs);
};

export const saveEncryptedConversation = async (userId, conversationData) => {
  try {
    if (!userId || !conversationData) {
      throw new Error('User ID or conversation data missing for saveEncryptedConversation');
    }
    if (!conversationData.threadId) {
      throw new Error('threadId (sessionId) is missing in conversationData for saveEncryptedConversation');
    }
    if (!conversationData.encryptedData || !conversationData.iv || !conversationData.salt) {
      throw new Error('Encrypted data, IV, or Salt missing in conversationData');
    }

    const threadId = conversationData.threadId;
    const itemRef = doc(db, 'user_conversations', userId, 'items', threadId);
    const newEncryptionDate = new Date();

    // "New" means new to this user entirely — check both locations so a
    // re-encrypted legacy conversation never double-counts.
    const existingItem = await getDoc(itemRef);
    const inLegacy = (await readLegacyConversations(userId)).find(
      (c) => (c.threadId || c.id) === threadId,
    );
    const isNewConversation = !existingItem.exists() && !inLegacy;

    const previousDates =
      (existingItem.exists() ? existingItem.data()?.encryptedDates : inLegacy?.encryptedDates) || [];

    await setDoc(itemRef, {
      id: threadId,
      threadId,
      createdAt: newEncryptionDate,
      encryptedData: conversationData.encryptedData,
      iv: conversationData.iv,
      salt: conversationData.salt,
      sessionTitle: conversationData.sessionTitle || 'Encrypted Session',
      encryptedDates: [...previousDates, newEncryptionDate],
    });

    // If this conversation still has a stale copy in the legacy array, drop it so
    // the two can never disagree. Removing an entry only ever shrinks that
    // document, so this is safe even when it is close to the size limit.
    if (inLegacy) {
      const remaining = (await readLegacyConversations(userId)).filter(
        (c) => (c.threadId || c.id) !== threadId,
      );
      await setDoc(doc(db, 'user_conversations', userId), { conversations: remaining }, { merge: true });
    }

    // Count only AFTER the save succeeds, and only for a first-time encryption.
    // Incrementing beforehand meant every failed save still inflated the counter,
    // which for free users ate into the 5-conversation cap.
    if (isNewConversation) {
      await updateDoc(doc(db, 'users', userId), {
        encryptedConversationCount: increment(1),
      });
    }

  } catch (error) {
    // Rethrow with the original name/code intact — the caller reports it.
    throw error;
  }
};

/**
 * Delete one encrypted conversation. Clears BOTH locations — if only items/ were
 * cleared, a stale legacy copy would resurface on the next load and a
 * conversation the user believes they deleted would come back.
 */
export const deleteEncryptedConversation = async (userId, threadId) => {
  if (!userId || !threadId) throw new Error('userId and threadId are required');

  await deleteDoc(doc(db, 'user_conversations', userId, 'items', threadId)).catch(() => {});

  const legacy = await readLegacyConversations(userId);
  const remaining = legacy.filter((c) => (c.threadId || c.id) !== threadId);
  if (remaining.length !== legacy.length) {
    await setDoc(doc(db, 'user_conversations', userId), { conversations: remaining }, { merge: true });
  }

  // Keep the counter honest rather than letting it drift downward-only.
  const total = (await getEncryptedConversationsList(userId)).length;
  await updateDoc(doc(db, 'users', userId), { encryptedConversationCount: total }).catch(() => {});

  // Drop the flag on the session so the UI offers to encrypt it again.
  // updateDoc (not setDoc/merge) so a deleted session is never resurrected as a stub.
  await updateDoc(doc(db, 'user_sessions', userId, 'sessions', threadId), {
    is_conversation_encrypted: false,
    last_updated: serverTimestamp(),
  }).catch(() => {});

  return total;
};

/**
 * Remove every encrypted conversation for a user, both locations.
 * Firestore does NOT delete subcollections when a document is deleted, so
 * account deletion must call this or the conversations outlive the account.
 */
export const deleteAllEncryptedConversations = async (userId) => {
  if (!userId) return 0;
  const items = await getDocs(conversationItemsRef(userId));
  let deleted = 0;
  for (const d of items.docs) {
    await deleteDoc(d.ref);
    deleted += 1;
  }
  return deleted;
};


// ---------------------------------------------
// APPOINTMENT SUB-COLLECTION SERVICES
// ---------------------------------------------

/**
 * Fetch all appointments for a user from sub-collection:
 * /user_appointments/{uid}/appointments/{appointmentDocId}
 */
export const fetchUserAppointments = async (uid) => {
  if (!uid) return [];
  
  try {
    // Reference the sub-collection
    const appointmentsRef = collection(db, 'user_appointments', uid, 'appointments');
    const snapshot = await getDocs(appointmentsRef);
    const appointments = snapshot.docs.map((docSnap) => {
      return { id: docSnap.id, ...docSnap.data() };
    });
    return appointments;
  } catch (error) {
    //console.error('Error fetching user appointments:', error);
    return [];
  }
};

/**
 * Create a new appointment doc in sub-collection
 */
export const addUserAppointment = async (uid, appointment) => {
  if (!uid || !appointment) return null;

  try {
    // Reference to the parent document
    const userDocRef = doc(db, 'user_appointments', uid);

    // Ensure the parent document exists with the 'active' field set to "Y"
    await setDoc(userDocRef, { active: "Y" }, { merge: true });

    // Reference to the 'appointments' sub-collection
    const appointmentsRef = collection(db, 'user_appointments', uid, 'appointments');

    // Add the appointment to the sub-collection
    const docRef = await addDoc(appointmentsRef, appointment);

    return docRef.id; // Return the new document ID
  } catch (error) {
    //console.error('Error adding appointment:', error);
    throw error;
  }
};

/**
 * Update an existing appointment in the sub-collection
 */
export const updateUserAppointment = async (uid, appointmentId, updatedAppointment) => {
  if (!uid || !appointmentId || !updatedAppointment) return;

  try {
    const appointmentDocRef = doc(db, 'user_appointments', uid, 'appointments', appointmentId);

    // Add the updated fields and explicitly remove emailSent
    const updatedPayload = {
      ...updatedAppointment, // Spread the updated fields
      emailSent: deleteField(), // Remove the emailSent field
    };

    await updateDoc(appointmentDocRef, updatedPayload);
  } catch (error) {
    //console.error('Error updating appointment:', error);
    throw error;
  }
};

/**
 * Delete an appointment from the sub-collection
 */
export const deleteUserAppointment = async (uid, appointmentId) => {
  if (!uid || !appointmentId) return;

  try {
    const appointmentDocRef = doc(db, 'user_appointments', uid, 'appointments', appointmentId);
    await deleteDoc(appointmentDocRef);
  } catch (error) {
    //console.error('Error deleting appointment:', error);
    throw error;
  }
};

export const deleteOldAppointments = async (uid) => {
  if (!uid) return;

  try {
    const appointmentsRef = collection(db, "user_appointments", uid, "appointments");
    const snapshot = await getDocs(appointmentsRef);

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const oldAppointments = snapshot.docs.filter((docSnap) => {
      const appointmentData = docSnap.data();
      const appointmentDate = new Date(appointmentData.dateTime);
      return appointmentDate < thirtyDaysAgo;
    });

    for (const oldAppointment of oldAppointments) {
      const appointmentDocRef = doc(db, "user_appointments", uid, "appointments", oldAppointment.id);
      await deleteDoc(appointmentDocRef);
    }

  } catch (error) {
    //console.error("Error deleting old appointments:", error);
  }
};


export const initOrResetVoiceCredits = async (userId) => {
  if (!userId) return;

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  const currentDate = new Date().toISOString();

  if (!userSnap.exists()) {
    const defaultCredits = 300;
    await setDoc(userRef, {
      voice_credits: defaultCredits,
      last_credit_reset: currentDate,
    });
    return defaultCredits;
  }

  const data = userSnap.data();
  const isPro = data.is_pro || "N";
  const defaultCredits = isPro === "Y" ? 999999 : 300;

  let { voice_credits, last_credit_reset } = data;

  if (voice_credits === undefined) voice_credits = defaultCredits;
  if (!last_credit_reset) last_credit_reset = currentDate;

  const lastResetDate = new Date(last_credit_reset);
  const hoursSinceLastReset = (new Date().getTime() - lastResetDate.getTime()) / (1000 * 60 * 60);

  // Reset if 24 hours have passed AND current credits are below the default
  if (hoursSinceLastReset >= 24 && voice_credits < defaultCredits) {
    voice_credits = defaultCredits;
    last_credit_reset = currentDate;

    await updateDoc(userRef, {
      voice_credits,
      last_credit_reset,
      reset_log: arrayUnion({ timestamp: currentDate }),
    });
  }

  return voice_credits;
};





export const decrementVoiceCredits = async (userId, creditsToDeduct = 1) => {
  if (!userId) return;

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    voice_credits: increment(-creditsToDeduct),
  });
};

export const getVoiceCredits = async (userId) => {
  if (!userId) return 0;

  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);

  if (!snap.exists()) return 0;

  const data = snap.data();
  return data.voice_credits || 0;
};


// Free Voice Credits

export const initOrResetVoiceCreditsFree = async (userId) => {
  if (!userId) return;
  
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  const defaultCredits = 50; // Default = 50 credits per day for free users (50 x 6s = 5 min)
  const currentDate = new Date().toISOString();

  if (!userSnap.exists()) {
    await setDoc(userRef, {
      voice_credits_free: defaultCredits,
      last_credit_reset_free: currentDate,
    });
    return defaultCredits;
  }

  const data = userSnap.data();
  let { voice_credits_free, last_credit_reset_free } = data;

  let updateFields = {};

  if (voice_credits_free === undefined) {
    voice_credits_free = defaultCredits;
    updateFields.voice_credits_free = defaultCredits;
  }
  if (!last_credit_reset_free) {
    last_credit_reset_free = currentDate;
    updateFields.last_credit_reset_free = currentDate;
  }

  const lastResetDate = new Date(last_credit_reset_free);
  const hoursSinceLastReset = (new Date().getTime() - lastResetDate.getTime()) / (1000 * 60 * 60);

  // Reset if 24 hours have passed AND current credits are below the default
  if (hoursSinceLastReset >= 24 && voice_credits_free < defaultCredits) {
    voice_credits_free = defaultCredits;
    last_credit_reset_free = currentDate;

    updateFields.voice_credits_free = defaultCredits;
    updateFields.last_credit_reset_free = currentDate;
    updateFields.reset_log = arrayUnion({ timestamp: currentDate });
  }

  if (Object.keys(updateFields).length > 0) {
    await updateDoc(userRef, updateFields);
  }

  return voice_credits_free;
};



export const decrementVoiceCreditsFree = async (userId, creditsToDeduct = 1) => {
  if (!userId) return;

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    voice_credits_free: increment(-creditsToDeduct),
  });
};

export const getVoiceCreditsFree = async (userId) => {
  if (!userId) return 0;

  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);

  if (!snap.exists()) return 0;

  const data = snap.data();
  return data.voice_credits_free || 0;
};

export const fetchResetLogs = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const docSnap = await getDoc(userRef);
  return docSnap.exists() ? docSnap.data().reset_log || [] : [];
};


export const logErrorToFirestore = async (errorDetails) => {
  try {
    // Add a timestamp from Firestore (serverTimestamp) or just use new Date().
    await addDoc(collection(db, 'error_logs'), {
      ...errorDetails,
      timestamp: serverTimestamp(),
    });
    // console.log('Error logged to Firestore successfully');
  } catch (error) {
    //console.error('Error logging to Firestore:', error);
  }
};


/**
* Updates a specific session document in Firestore with encrypted history data and metadata.
* Assumes the session document already exists.
*
* @param {string} userId - The ID of the user who owns the session.
* @param {string} sessionId - The ID of the specific session document to update.
* @param {object} encryptedPayload - An object containing the encrypted data and necessary metadata.
* @param {string} encryptedPayload.encryptedData - The encrypted history string (derived from response_id_history).
* @param {string} encryptedPayload.iv - The initialization vector used for this specific encryption instance.
* @param {string} encryptedPayload.salt - The salt used for key derivation during this specific encryption instance.
* @returns {Promise<void>} A promise that resolves when the update is complete.
* @throws {Error} Throws an error if input validation fails or the Firestore update fails.
*/
export const updateSessionWithEncryptedHistory = async (userId, sessionId, encryptedPayload) => {
   // 1. Validate Inputs
   if (!userId) {
       throw new Error("User ID is required to update session history.");
   }
   if (!sessionId) {
       throw new Error("Session ID is required to update session history.");
   }
   if (!encryptedPayload || typeof encryptedPayload !== 'object') {
       throw new Error("Encrypted payload object is required.");
   }
   if (typeof encryptedPayload.encryptedData !== 'string' || !encryptedPayload.encryptedData) {
       throw new Error("Encrypted data string is missing or invalid in the payload.");
   }
   if (typeof encryptedPayload.iv !== 'string' || !encryptedPayload.iv) {
       throw new Error("Initialization vector (iv) string is missing or invalid in the payload.");
   }
   if (typeof encryptedPayload.salt !== 'string' || !encryptedPayload.salt) {
       throw new Error("Salt string is missing or invalid in the payload.");
   }

   // 2. Construct Document Reference
   // Reference follows the structure: /user_sessions/{userId}/sessions/{sessionId}
   const sessionDocRef = doc(db, 'user_sessions', userId, 'sessions', sessionId);

   // 3. Prepare Data for Update
   const updateData = {
       encrypted_response_history: encryptedPayload.encryptedData,
       encryption_iv: encryptedPayload.iv,
       encryption_salt: encryptedPayload.salt,
       last_updated: serverTimestamp(), // Update the timestamp to reflect this change
   };

   // 4. Perform Firestore Update Operation
   try {
       await updateDoc(sessionDocRef, updateData);
      //  console.log(`Session document ${sessionId} successfully updated with encrypted history for user ${userId}.`);
   } catch (error) {
       //console.error(`Failed to update session ${sessionId} for user ${userId} with encrypted history:`, error);
       // Re-throw a more specific error for the caller
       throw new Error(`Firestore update failed: ${error.message}`);
   }
};


/**
* Fetches the data for a specific session document.
* @param {string} userId - The ID of the user.
* @param {string} sessionId - The ID of the session document.
* @returns {Promise<object|null>} The session data object if found, otherwise null.
*/
export const getSessionDataFromFirestore = async (userId, sessionId) => {
   if (!userId || !sessionId) {
       //console.error("User ID and Session ID are required to fetch session data.");
       return null;
   }
   const sessionDocRef = doc(db, 'user_sessions', userId, 'sessions', sessionId);
   try {
       const docSnap = await getDoc(sessionDocRef);
       if (docSnap.exists()) {
           return docSnap.data();
       } else {
          //  console.log(`Session document ${sessionId} not found for user ${userId}.`);
           return null;
       }
   } catch (error) {
       //console.error(`Error fetching session data for session ${sessionId}, user ${userId}:`, error);
       return null; // Return null on error
   }
};

export const updateSessionWithEncryptedText = async (userId, sessionId, encryptedTextPayload, sessionTitle) => {
  if (!userId || !sessionId || !encryptedTextPayload || !encryptedTextPayload.encryptedData || !encryptedTextPayload.iv || !encryptedTextPayload.salt) {
      throw new Error("Missing required parameters for updating encrypted session text.");
  }
  if (!sessionTitle) {
      // console.warn("sessionTitle not provided for updateSessionWithEncryptedText, using default.");
  }

  // This is the reference to the session metadata in user_sessions
  const sessionMetadataDocRef = doc(db, 'user_sessions', userId, 'sessions', sessionId);

  try {
      // 1. Prepare data for storing in 'user_conversations'
      const conversationDataForStorage = {
        encryptedData: encryptedTextPayload.encryptedData,
        iv: encryptedTextPayload.iv,
        salt: encryptedTextPayload.salt,
        threadId: sessionId, // The original sessionId becomes threadId here
        sessionTitle: sessionTitle || "Encrypted Session",
        date: new Date().toISOString(), // Current date for the conversation entry
      };

      // Call the function to save to user_conversations
      await saveEncryptedConversation(userId, conversationDataForStorage);

      // 2. Update the session document in 'user_sessions' to flag it
      //    and remove old fields if they were used previously.
      const updateDataForSessionMetadata = {
          is_conversation_encrypted: true,   // The new flag
          encrypted_conversation_text: deleteField(), // Remove old field
          text_encryption_iv: deleteField(),          // Remove old field
          text_encryption_salt: deleteField(),        // Remove old field
          last_updated: serverTimestamp(),
      };
      await updateDoc(sessionMetadataDocRef, updateDataForSessionMetadata);

      // console.log(`Session ${sessionId} flagged as encrypted in user_sessions, and actual data stored in user_conversations.`);

  } catch (error) {
      // Preserve the original error's name/code so the caller can tell a wrong
      // access key (OperationError) apart from a storage failure (FirebaseError).
      const wrapped = new Error(`Overall encryption and flagging process failed: ${error.message}`);
      wrapped.name = error?.name || 'Error';
      wrapped.code = error?.code;
      wrapped.cause = error;
      throw wrapped;
  }
};


export const addCompletedProgramForUser = async (uid, topic) => {
  if (!uid || !topic) return;
  try {
    const ref = doc(db, 'user_programs', uid);
    await setDoc(ref, { completed: arrayUnion(topic) }, { merge: true });
  } catch (err) {
    //console.error('Error saving completed program:', err);
  }
};

/**
 * Get the array of completed topics for a user.
 */
export const fetchCompletedProgramsForUser = async (uid) => {
  if (!uid) return [];
  try {
    const ref = doc(db, 'user_programs', uid);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data().completed || [] : [];
  } catch (err) {
    //console.error('Error fetching completed programs:', err);
    return [];
  }
};


export const getUserAnalysisSessionCount = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const minSessionsForAnalysis = 5;

  if (!user) {
    throw new Error('Authentication required to fetch session count.');
  }

  const userId = user.uid;

  try {
    // Counts items/ and the legacy array together, deduped by threadId.
    const count = (await getEncryptedConversationsList(userId)).length;
    return { count, min_required: minSessionsForAnalysis };
  } catch (error) {
    //console.error(`Error counting conversations for user ${userId}:`, error);
    throw new Error(`Failed to retrieve session count: ${error.message}`);
  }
};


/**
 * Saves a deep analysis report for a user.
 * @param {string} uid - The user's unique ID.
 * @param {string} reportText - The markdown text of the analysis report.
 * @returns {Promise<string>} The ID of the newly saved report.
 */
export const saveDeepAnalysisReport = async (uid, reportText) => {
  if (!uid || !reportText) {
    throw new Error("User ID and report text are required to save the report.");
  }
  try {
    const reportsCollectionRef = collection(db, 'user_analysis', uid, 'reports');
    const newReportDocRef = await addDoc(reportsCollectionRef, {
      userId: uid, // Store uid for potential cross-checking, though path already has it
      text: reportText,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return newReportDocRef.id;
  } catch (error) {
    //console.error(`Error saving deep analysis report for user ${uid}:`, error);
    throw new Error(`Failed to save report: ${error.message}`);
  }
};

/**
 * Loads a specific deep analysis report for a user.
 * @param {string} uid - The user's unique ID.
 * @param {string} reportId - The ID of the report to load.
 * @returns {Promise<Object|null>} The report data (e.g., { text, createdAt }) or null if not found/no access.
 */
export const loadDeepAnalysisReport = async (uid, reportId) => {
  if (!uid || !reportId) {
    throw new Error("User ID and Report ID are required to load the report.");
  }
  try {
    const reportDocRef = doc(db, 'user_analysis', uid, 'reports', reportId);
    const reportSnap = await getDoc(reportDocRef);

    if (reportSnap.exists()) {
      const reportData = reportSnap.data();
      // Optional: verify the userId field in the document for an extra layer,
      // though the path-based security rules should primarily handle access control.
      if (reportData.userId === uid) {
        return {
          id: reportSnap.id,
          text: reportData.text,
          createdAt: reportData.createdAt?.toDate ? reportData.createdAt.toDate() : null, // Convert Firestore Timestamp
          // include other fields if needed
        };
      } else {
        // console.warn(`Attempt to load report ${reportId} for user ${uid}, but document's userId mismatch.`);
        throw new Error("Report data integrity issue."); // Or return null if preferred for silent failure
      }
    } else {
      return null; // Report not found
    }
  } catch (error) {
    //console.error(`Error loading deep analysis report ${reportId} for user ${uid}:`, error);
    // Re-throw specific errors or a generic one
    if (error.message === "Report data integrity issue.") throw error;
    throw new Error(`Failed to load report: ${error.message}`);
  }
};

export const loadAllUserAnalysisReports = async (uid) => {
  if (!uid) {
    // console.warn("User ID is required to load analysis reports.");
    return []; // Or throw new Error("User ID is required.");
  }
  try {
    const reportsCollectionRef = collection(db, 'user_analysis', uid, 'reports');
    const q = query(reportsCollectionRef, orderBy('createdAt', 'desc')); // Order by newest first
    const querySnapshot = await getDocs(q);
    const reports = querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
    return reports;
  } catch (error) {
    //console.error(`Error loading all analysis reports for user ${uid}:`, error);
    throw new Error(`Failed to load reports: ${error.message}`);
  }
};

/**
 * Deletes a specific character analysis report for a user.
 * @param {string} uid - The user's unique ID.
 * @param {string} reportId - The ID of the report to delete.
 * @returns {Promise<void>}
 */
export const deleteUserAnalysisReport = async (uid, reportId) => {
  if (!uid || !reportId) {
    throw new Error("User ID and Report ID are required to delete the report.");
  }
  try {
    const reportDocRef = doc(db, 'user_analysis', uid, 'reports', reportId);
    await deleteDoc(reportDocRef);
  } catch (error) {
    //console.error(`Error deleting analysis report ${reportId} for user ${uid}:`, error);
    throw new Error(`Failed to delete report: ${error.message}`);
  }
};

export const getDeepAnalysisUsage = async (uid) => {
  if (!uid) throw new Error("User ID is required to get usage data.");

  const userDocRef = doc(db, 'users', uid);
  const userDocSnap = await getDoc(userDocRef);

  const currentCycle = new Date().toISOString().slice(0, 7); // YYYY-MM format

  if (userDocSnap.exists()) {
    const userData = userDocSnap.data();
    const usageData = userData.deepAnalysisUsage;

    if (usageData && usageData.cycleMonthYear === currentCycle) {
      return {
        count: usageData.monthlyCount || 0,
        cycleMonthYear: usageData.cycleMonthYear,
        needsReset: false,
      };
    }
  }
  // If no usage data, or if cycleMonthYear is different (or old), it's a new/reset cycle.
  return {
    count: 0,
    cycleMonthYear: currentCycle,
    needsReset: true, // Indicates either first time use or new month
  };
};

/**
 * Records a deep analysis usage for the user.
 * If it's a new cycle (new month or first use), it resets the count.
 * @param {string} uid - The user's unique ID.
 * @param {number} previousCountInCycle - The count *before* this new usage in the current cycle.
 * @param {string} currentCycleMonthYear - The current cycle string (e.g., "YYYY-MM").
 * @returns {Promise<void>}
 */
export const recordDeepAnalysisUsage = async (uid, previousCountInCycle, currentCycleMonthYear) => {
  if (!uid) throw new Error("User ID is required to record usage.");

  const userDocRef = doc(db, 'users', uid);
  const newCount = previousCountInCycle + 1;

  const usageUpdate = {
    deepAnalysisUsage: {
      monthlyCount: newCount,
      lastUsedAt: serverTimestamp(),
      cycleMonthYear: currentCycleMonthYear,
    },
  };

  // Use setDoc with merge: true to create/update the user document safely,
  // specifically updating or creating the deepAnalysisUsage map.
  try {
    await setDoc(userDocRef, usageUpdate, { merge: true });
  } catch (error) {
    //console.error("Error recording deep analysis usage:", error);
    throw new Error("Could not update usage data.");
  }
};


export const markOnboardingComplete = async (userId) => {
  if (!userId) {
    // console.error("User ID is required to mark onboarding as complete.");
    return;
  }
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      onboarding_completed: true,
      last_updated: serverTimestamp(),
    });
    // console.log(`Onboarding marked as complete for user ${userId}.`);
  } catch (error) {
    // console.error("Error marking onboarding as complete:", error);
  }
};


/**
 * Checks if the early feedback dialog has been shown to the user.
 * @param {string} userId The user's unique ID.
 * @returns {Promise<boolean>} True if the field `early_feedback_shown` is true, otherwise false.
 */
export const hasShownEarlyFeedback = async (userId) => {
  if (!userId) return true; // Default to true to prevent showing for non-logged-in users
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      return userDoc.data().early_feedback_shown === true;
    }
    return false; // User doc doesn't exist, so they haven't seen it
  } catch (error) {
    // console.error("Error checking early_feedback_shown:", error);
    return true; // Fail safe to avoid spamming the user on error
  }
};

/**
 * Marks the early feedback dialog as shown for a user in Firestore.
 * @param {string} userId The user's unique ID.
 * @returns {Promise<void>}
 */
export const markEarlyFeedbackAsShown = async (userId) => {
  if (!userId) return;
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { early_feedback_shown: true }, { merge: true });
  } catch (error) {
    // console.error("Error setting early_feedback_shown:", error);
  }
};